import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Layers, UserPlus, Loader2, RefreshCw, Filter, X, Check,
  AlertTriangle, FolderSync, Users, Film, Search, ChevronDown,
  Wrench, Zap,
} from 'lucide-react';
import WorkforceBadge from '@/components/WorkforceBadge';
import { formatApiError } from '@/lib/apiClient';

/**
 * AdminAllotmentPage — PM-facing CBA allotment grid.
 *
 * Usage:
 *   <AdminAllotmentPage />                               — standalone page (uses own project picker)
 *   <AdminAllotmentPage embeddedProjectId={projectId} /> — embedded inside the legacy Allotment Page's CBA tab (project picker hidden)
 *
 * Founder fix (Feb 18, 2026):
 * Before this page, PMs had two parallel allotment tools that didn't
 * talk to each other:
 *   • ShotAllotmentPage created NEW tasks linked by shot_id — the new
 *     Animator Allotment Sheet (cba_clip_id-linked) ignored them.
 *   • cba/manual-ingest/<pid> spawned Layout+Animation tasks BUT they
 *     stayed in 'unassigned' forever because no UI showed them.
 *
 * This page closes the loop: it pulls every CBA clip on the project
 * (from /animator/allotment/admin/clips), shows current assignees,
 * and lets PMs assign or reassign each Layout/Animation task to the
 * right artist (in-house, freelancer, or vendor producer) in one
 * place. A "Scan NAS for manual cuts" button calls the existing
 * /cba/manual-ingest endpoint so PMs don't have to dig through the
 * Emergency Admin page.
 */
export default function AdminAllotmentPage({ embeddedProjectId = null }) {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(embeddedProjectId || '');
  const isEmbedded = !!embeddedProjectId;
  const [data, setData] = useState(null);
  const [workforce, setWorkforce] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [filter, setFilter] = useState('unassigned'); // all|unassigned|assigned
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(null); // {task_id, stage}
  const [busyTaskId, setBusyTaskId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUser, setBulkUser] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Founder ask (Feb 28, 2026): "Allow me to multi-select 5 shots and
  // assign them all to one artist in one go instead of repeating the
  // same picker five times." Selection is a Set of cba_clip_ids scoped
  // to a single `bulkStage` (Layout / Animation / Both) so we never
  // accidentally cross-assign Layout work to an Animation artist.
  const [bulkStage, setBulkStage] = useState('layout'); // layout | animation | both
  const [selectedClipIds, setSelectedClipIds] = useState(() => new Set());
  const [bulkBusyAction, setBulkBusyAction] = useState(null); // 'assign' | 'unassign'
  const [workload, setWorkload] = useState(null);
  const [reprobing, setReprobing] = useState(false);
  const [reprobeResult, setReprobeResult] = useState(null);

  useEffect(() => {
    if (isEmbedded) return; // parent supplies projectId
    api.get('/projects').then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.projects || []);
      setProjects(list);
      if (list.length && !projectId) setProjectId(list[0].id);
    }).catch(() => setProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isEmbedded]);

  // When parent changes embeddedProjectId, sync local state.
  useEffect(() => {
    if (isEmbedded && embeddedProjectId !== projectId) {
      setProjectId(embeddedProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedProjectId]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [c, w, wl] = await Promise.all([
        api.get(`/animator/allotment/admin/clips?project_id=${projectId}`),
        api.get(`/animator/allotment/admin/workforce?project_id=${projectId}`),
        api.get(`/animator/allotment/admin/workload-summary?project_id=${projectId}`)
          .catch(() => ({ data: null })),
      ]);
      setData(c.data);
      setWorkforce(w.data || []);
      setWorkload(wl.data);
    } catch (e) {
      setData(null);
      setWorkforce([]);
      setWorkload(null);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  const scanNAS = async () => {
    if (!projectId) return;
    setScanning(true); setScanResult(null);
    try {
      const r = await api.post(`/cba/manual-ingest/${projectId}`);
      setScanResult(r.data);
      await load();
    } catch (e) {
      setScanResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setScanning(false);
    }
  };

  const reprobeMetadata = async () => {
    if (!projectId) return;
    setReprobing(true); setReprobeResult(null);
    try {
      const r = await api.post('/animator/allotment/admin/reprobe',
        { project_id: projectId, only_unprobed: true });
      // Founder fix Feb 2026: large reprobes (>5 clips) dispatch in
      // background to defeat Cloudflare's 100s edge timeout. If we got
      // a job_id back, poll for completion before showing the result.
      if (r.data && r.data.job_id) {
        const jobId = r.data.job_id;
        // Show initial dispatch status so the user knows it's running.
        setReprobeResult({ ...r.data, _polling: true });
        // Poll every 3s until job completes or 15 minutes pass.
        // Founder bug Jun 14 2026 Thumbelina: 36 clips × ~30 s probe
        // timeout × concurrency=2 ≈ 9 min worst case. 5-min timeout
        // fired BEFORE the worker finished → user saw misleading
        // "complete" banner with 0/0 counts that were really the
        // initial dispatch values.
        const start = Date.now();
        const TIMEOUT_MS = 15 * 60 * 1000;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise(res => setTimeout(res, 3000));
          if (Date.now() - start > TIMEOUT_MS) {
            // One final poll to capture late completion just in case.
            try {
              const fin = await api.get(
                `/animator/allotment/admin/reprobe-status/${jobId}`);
              if (fin.data && fin.data.status !== 'running') {
                setReprobeResult(fin.data);
                break;
              }
              setReprobeResult({
                ok: false, _timed_out: true,
                ...fin.data,
                tldr: `Reprobe still running after 15 min (${fin.data?.probed_ok || 0} / ${fin.data?.total_targets || 0} done). Refresh the page to see updated metadata — the background job will keep going.`,
              });
            } catch {
              setReprobeResult({
                ok: false, _timed_out: true,
                tldr: 'Reprobe still running after 15 min — refresh the page in a minute to see updated metadata.',
              });
            }
            break;
          }
          try {
            const s = await api.get(
              `/animator/allotment/admin/reprobe-status/${jobId}`);
            if (s.data && s.data.status !== 'running') {
              setReprobeResult(s.data);
              break;
            }
            // Still running — update progress indicator.
            setReprobeResult({ ...s.data, _polling: true });
          } catch (pollErr) {
            // Transient network blip → keep retrying.
          }
        }
      } else {
        setReprobeResult(r.data);
      }
      await load();
    } catch (e) {
      const fe = formatApiError(e);
      // Auth errors are handled globally by the AuthContext (toast +
      // redirect). Don't ALSO pollute the banner with the same message
      // — that's the bug we're fixing system-wide.
      if (fe.kind === 'auth_expired') {
        setReprobeResult(null);
        return;
      }
      setReprobeResult({
        ok: false,
        kind: fe.kind,
        error_title: fe.title,
        error_message: fe.message,
        error_hint: fe.hint,
        retriable: fe.retriable,
      });
    } finally {
      setReprobing(false);
    }
  };

  // Backfill NAS paths for legacy clips with NULL `nas_video_rel`
  // (Founder bug Feb 2026 "0 of 0 targets"). Scans the project's NAS
  // CBA folder + matches each broken clip's label to a physical file
  // or folder, then writes `nas_video_rel` back. After this, re-probe
  // will pick them up.
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const backfillNasPaths = async () => {
    if (!projectId) return;
    setBackfilling(true); setBackfillResult(null);
    try {
      const r = await api.post(
        `/animator/allotment/admin/backfill-nas-paths/${projectId}`);
      setBackfillResult(r.data);
      await load();
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') {
        setBackfillResult(null);
        return;
      }
      setBackfillResult({
        ok: false,
        kind: fe.kind,
        error_title: fe.title,
        error_message: fe.message,
        error_hint: fe.hint,
        retriable: fe.retriable,
      });
    } finally {
      setBackfilling(false);
    }
  };

  // Founder fix May 14, 2026: ffprobe's nb_frames lies for many codecs
  // (SH_006: 2.465s × 24fps = 59 frames but the stored value was 96 →
  // 63% over-billing risk). This button re-computes frame_count from
  // round(duration × fps) on every clip + propagates to tasks &
  // shot_registry. Idempotent.
  const [recomputingFrames, setRecomputingFrames] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState(null);
  const recomputeFrameCounts = async () => {
    if (!projectId) return;
    setRecomputingFrames(true); setRecomputeResult(null);
    try {
      const r = await api.post('/animator/allotment/admin/recompute-frame-counts',
        { project_id: projectId });
      setRecomputeResult(r.data);
      await load();
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') {
        setRecomputeResult(null);
        return;
      }
      setRecomputeResult({
        ok: false,
        kind: fe.kind,
        error_title: fe.title,
        error_message: fe.message,
        error_hint: fe.hint,
      });
    } finally {
      setRecomputingFrames(false);
    }
  };

  // ── Founder root-cause Jun 13 2026: "Fix Everything" cascade ──
  // PMs kept clicking "Re-probe" first, getting "0 of 0 targets"
  // (because clips had NULL nas_video_rel), then either giving up or
  // clicking the wrong button next. This one button runs the right
  // sequence: backfill paths → reprobe metadata. The banner shows
  // step-by-step progress so the user always knows what's happening.
  const [cascadeStep, setCascadeStep] = useState(null);   // 'backfill' | 'reprobe' | null
  const [cascadeResult, setCascadeResult] = useState(null);
  const cascadeFixMetadata = async () => {
    if (!projectId) return;
    setCascadeResult(null);
    setBackfillResult(null);
    setReprobeResult(null);
    // ── Step 1: backfill NAS paths ──
    setCascadeStep('backfill');
    let backfill;
    try {
      const r = await api.post(
        `/animator/allotment/admin/backfill-nas-paths/${projectId}`);
      backfill = r.data;
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') {
        setCascadeStep(null);
        return;
      }
      setCascadeResult({
        ok: false, step: 'backfill',
        error_title: fe.title, error_message: fe.message,
        error_hint: fe.hint,
      });
      setCascadeStep(null);
      return;
    }
    // ── Step 2: reprobe metadata (only_unprobed=true) ──
    setCascadeStep('reprobe');
    let reprobe;
    try {
      const r = await api.post('/animator/allotment/admin/reprobe',
        { project_id: projectId, only_unprobed: true });
      reprobe = r.data;
      // Background job — poll for completion.
      if (reprobe && reprobe.job_id) {
        const jobId = reprobe.job_id;
        const start = Date.now();
        // Founder bug Jun 14 2026 Thumbelina: 5-min timeout fired
        // before the 36-clip job finished, so the final banner showed
        // 0 ok / 0 failed (the INITIAL dispatch response). Bumped to
        // 15 min and added a final poll on timeout so a job that
        // finishes between iterations isn't reported as "still running".
        const TIMEOUT_MS = 15 * 60 * 1000;
        let timedOut = false;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise(res => setTimeout(res, 3000));
          if (Date.now() - start > TIMEOUT_MS) {
            timedOut = true;
            try {
              const fin = await api.get(
                `/animator/allotment/admin/reprobe-status/${jobId}`);
              if (fin.data && fin.data.status !== 'running') {
                reprobe = fin.data;       // capture late completion
                timedOut = false;
              } else if (fin.data) {
                reprobe = { ...fin.data, _timed_out: true };
              }
            } catch (_e) { /* keep timedOut=true */ }
            break;
          }
          try {
            const s = await api.get(
              `/animator/allotment/admin/reprobe-status/${jobId}`);
            if (s.data && s.data.status !== 'running') {
              reprobe = s.data;
              break;
            }
            // Live progress in the banner — surface the running counts
            // so the founder can see clips being probed in real time.
            setCascadeResult({
              ok: true, step: 'reprobe', running: true,
              backfill,
              reprobe_progress: s.data,
            });
          } catch (_pollErr) { /* transient */ }
        }
        if (timedOut) {
          const progressCount = reprobe?.probed_ok || 0;
          const failedCount = reprobe?.probe_failed || 0;
          const totalCount = reprobe?.total_targets || 0;
          setCascadeResult({
            ok: false, step: 'reprobe', _timed_out: true,
            backfill,
            reprobe_progress: reprobe,
            error_title: 'Reprobe still running',
            error_message: (
              `Progress so far: ${progressCount} probed · ${failedCount} failed (of ${totalCount} targets). ` +
              `The 15-minute UI polling window expired but the job is STILL RUNNING in the background. ` +
              `Refresh the page in 1-2 minutes to see the final counts.`
            ),
            error_hint: 'No data is lost. The worker continues even after the UI stops polling.',
          });
          setCascadeStep(null);
          await load();
          return;
        }
      }
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') {
        setCascadeStep(null);
        return;
      }
      setCascadeResult({
        ok: false, step: 'reprobe',
        backfill,
        error_title: fe.title, error_message: fe.message,
        error_hint: fe.hint,
      });
      setCascadeStep(null);
      return;
    }
    setCascadeResult({ ok: true, step: 'done', backfill, reprobe });
    setCascadeStep(null);
    await load();
  };

  const assign = async (taskId, userId) => {
    setBusyTaskId(taskId);
    try {
      await api.post('/animator/allotment/admin/assign', {
        task_id: taskId,
        assign_to_user_id: userId,
      });
      setPickerOpen(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Assign failed');
    } finally {
      setBusyTaskId(null);
    }
  };

  const unassign = async (taskId) => {
    setBusyTaskId(taskId);
    try {
      await api.post('/animator/allotment/admin/assign', {
        task_id: taskId,
        assign_to_user_id: null,
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Unassign failed');
    } finally {
      setBusyTaskId(null);
    }
  };

  const filteredClips = useMemo(() => {
    if (!data?.clips) return [];
    let rows = data.clips;
    if (filter === 'unassigned') {
      rows = rows.filter(c =>
        (c.layout && !c.layout.assigned_to_user_id) ||
        (c.animation && !c.animation.assigned_to_user_id));
    } else if (filter === 'assigned') {
      rows = rows.filter(c =>
        (c.layout?.assigned_to_user_id) || (c.animation?.assigned_to_user_id));
    }
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(c => (c.label || '').toLowerCase().includes(q));
    }
    return rows;
  }, [data, filter, search]);

  // Reset selection whenever the project, stage scope, or filter changes —
  // keeps users from accidentally bulk-assigning across stages/projects.
  useEffect(() => {
    setSelectedClipIds(new Set());
  }, [projectId, bulkStage, filter]);

  // Which clips can the user check for the currently-active bulk stage?
  // For "layout": clips that have a layout task. Same for animation.
  // For "both": clips that have at least one of layout/animation.
  const eligibleSelectableIds = useMemo(() => {
    return filteredClips
      .filter(c => {
        if (bulkStage === 'layout') return !!c.layout;
        if (bulkStage === 'animation') return !!c.animation;
        return !!(c.layout || c.animation);
      })
      .map(c => c.cba_clip_id);
  }, [filteredClips, bulkStage]);

  const toggleRow = (cid) => {
    setSelectedClipIds(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const allSelected = eligibleSelectableIds.length > 0
    && eligibleSelectableIds.every(id => selectedClipIds.has(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedClipIds(new Set());
    } else {
      setSelectedClipIds(new Set(eligibleSelectableIds));
    }
  };

  const bulkAssign = async () => {
    if (!bulkUser || selectedClipIds.size === 0) return;
    setBulkBusy(true); setBulkBusyAction('assign');
    try {
      await api.post('/animator/allotment/admin/bulk-assign', {
        project_id: projectId,
        assignments: Array.from(selectedClipIds).map(cid => ({
          cba_clip_id: cid, stage: bulkStage, assign_to_user_id: bulkUser,
        })),
      });
      setBulkOpen(false);
      setBulkUser('');
      setSelectedClipIds(new Set()); // founder ask: clear selection on success
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkBusy(false); setBulkBusyAction(null);
    }
  };

  const bulkUnassign = async () => {
    if (selectedClipIds.size === 0) return;
    if (!window.confirm(
      `Unassign ${bulkStage === 'both' ? 'Layout + Animation' : bulkStage} on ${selectedClipIds.size} shot(s)?`
    )) return;
    setBulkBusy(true); setBulkBusyAction('unassign');
    try {
      await api.post('/animator/allotment/admin/bulk-assign', {
        project_id: projectId,
        assignments: Array.from(selectedClipIds).map(cid => ({
          cba_clip_id: cid, stage: bulkStage, assign_to_user_id: null,
        })),
      });
      setSelectedClipIds(new Set());
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk unassign failed');
    } finally {
      setBulkBusy(false); setBulkBusyAction(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-allotment-page">
      {/* ── HEADER ─── (hidden when embedded — parent provides context) */}
      {!isEmbedded && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white tracking-tight">
              CBA Allotment Console
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              Assign Layout &amp; Animation tasks to animators, freelancers, or vendor producers — one place, no duplicate tasks.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
              data-testid="admin-allotment-project-select"
              className="h-9 px-3 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200"
            >
              <option value="">Select project…</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={load}
              disabled={loading || !projectId}
              data-testid="admin-allotment-refresh"
              className="h-9 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs rounded-md flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={scanNAS}
              disabled={scanning || !projectId}
              data-testid="admin-allotment-scan-nas"
              className="h-9 px-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50"
              title="Scan 004_Cut_by_Animatics on the NAS for shots dropped by the editor and auto-populate Layout/Animation tasks"
            >
              {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderSync className="w-3.5 h-3.5" />}
              {scanning ? 'Scanning…' : 'Scan NAS for new cuts'}
            </button>
            <button
              onClick={recomputeFrameCounts}
              disabled={recomputingFrames || !projectId}
              data-testid="admin-allotment-recompute-frames"
              title="Fix stale frame counts using duration × fps (founder bug May 14, 2026 — ffprobe nb_frames was over-reporting frames by 60%+ on some codecs)"
              className="h-9 px-3 bg-violet-500 hover:bg-violet-400 text-white text-xs font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50"
            >
              {recomputingFrames ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {recomputingFrames ? 'Fixing frames…' : 'Recompute frames'}
            </button>
          </div>
        </div>
      )}

      {/* When embedded, surface the Scan + Refresh buttons inline above
          the summary cards so the PM still has one-click access. */}
      {isEmbedded && (
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-auto">
            <Film className="w-3 h-3 inline mr-1" />
            CBA · Cut-By-Animatics
            {data && (
              <span className="ml-2 text-zinc-600">· auto-scan every 2 min</span>
            )}
          </span>
          <button
            onClick={load}
            disabled={loading || !projectId}
            data-testid="admin-allotment-refresh"
            className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] rounded-md flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={scanNAS}
            disabled={scanning || !projectId}
            data-testid="admin-allotment-scan-nas"
            className="h-8 px-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[11px] font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50"
            title="Re-scan 004_Cut_by_Animatics on the NAS now"
          >
            {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderSync className="w-3 h-3" />}
            {scanning ? 'Scanning…' : 'Re-scan NAS now'}
          </button>
          <button
            onClick={recomputeFrameCounts}
            disabled={recomputingFrames || !projectId}
            data-testid="admin-allotment-recompute-frames"
            title="Fix stale frame counts (duration × fps)"
            className="h-8 px-3 bg-violet-500 hover:bg-violet-400 text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50"
          >
            {recomputingFrames ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {recomputingFrames ? 'Fixing…' : 'Recompute frames'}
          </button>
        </div>
      )}

      {/* ── SCAN RESULT BANNER ─── */}
      {scanResult && (
        <div
          className={`rounded-md p-3 text-xs ${scanResult.ok
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
            : 'bg-red-500/10 border border-red-500/30 text-red-200'}`}
          data-testid="admin-allotment-scan-result"
        >
          {scanResult.ok ? (
            <span>
              <Check className="w-3.5 h-3.5 inline mr-1" />
              Scanned {scanResult.cba_dir}. Created {scanResult.clips_created} new clips ·{' '}
              {scanResult.tasks_created} pipeline tasks ·{' '}
              {scanResult.flat_files_scanned ?? 0} flat files · {scanResult.folders_scanned} folders.{' '}
              {scanResult.clips_skipped_existing > 0 && (
                <span className="text-zinc-400">({scanResult.clips_skipped_existing} already ingested)</span>
              )}
            </span>
          ) : (
            <span>
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              {scanResult.error || 'Scan failed'}
            </span>
          )}
        </div>
      )}

      {/* ── METADATA DIAGNOSTIC BANNER (rewritten Jun 13 2026) ───
          Founder root-cause: producers kept clicking "Re-probe" first
          and getting "0 of 0 targets" (because the 31 clips had NULL
          nas_video_rel — those clips need a BACKFILL first, not a
          reprobe). The banner now leads with one big "Fix Everything"
          button that runs backfill → reprobe in the correct order,
          with step-by-step progress. The individual buttons remain as
          escape hatches for advanced ops. */}
      {data && data.summary.total_clips > 0 && (() => {
        const unprobed = (data.clips || []).filter(c => !c.metadata_probed).length;
        if (unprobed === 0) return null;
        const cascading = !!cascadeStep;
        const cascadeLabel = cascadeStep === 'backfill'
          ? 'Step 1/2 · Repairing NAS paths…'
          : cascadeStep === 'reprobe'
            ? 'Step 2/2 · Probing video metadata…'
            : 'Fix everything · Backfill + Re-probe';
        return (
          <div
            className="rounded-md p-3 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200"
            data-testid="admin-allotment-unprobed-banner"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">
                  {unprobed} / {data.summary.total_clips} clips are missing duration / FPS / frame metadata.
                </p>
                <p className="text-[10px] text-amber-300/70 mt-0.5">
                  Some clips may also be missing their NAS path. Click <b>Fix everything</b> to repair both in the right order — no more guessing which button to press first.
                </p>
              </div>
              <button
                onClick={cascadeFixMetadata}
                disabled={cascading || backfilling || reprobing}
                data-testid="admin-allotment-cascade-fix"
                title="Runs Backfill NAS paths first, then Re-probe metadata — the correct order."
                className="h-8 px-4 bg-amber-400 hover:bg-amber-300 text-zinc-950 text-[11px] font-bold rounded flex items-center gap-1.5 disabled:opacity-60 shrink-0"
              >
                {cascading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Wrench className="w-3.5 h-3.5" />}
                {cascadeLabel}
              </button>
            </div>
            {/* Advanced escape-hatch buttons — secondary styling.
                Producers shouldn't need them in the normal flow. */}
            <details className="mt-2">
              <summary className="cursor-pointer text-[10px] text-amber-300/60 hover:text-amber-300 select-none">
                Advanced · run individual steps
              </summary>
              <div className="flex items-center gap-2 mt-1.5">
                <button
                  onClick={backfillNasPaths}
                  disabled={backfilling || cascading}
                  data-testid="admin-allotment-backfill-paths"
                  title="Repairs legacy clips where NAS path is missing. Run this FIRST if Re-probe says 'no targets'."
                  className="h-6 px-2.5 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400/40 text-sky-200 text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50"
                >
                  {backfilling
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <RefreshCw className="w-3 h-3" />}
                  {backfilling ? 'Backfilling…' : 'Backfill NAS paths'}
                </button>
                <button
                  onClick={reprobeMetadata}
                  disabled={reprobing || cascading}
                  data-testid="admin-allotment-reprobe"
                  className="h-6 px-2.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-200 text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50"
                >
                  {reprobing
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <RefreshCw className="w-3 h-3" />}
                  {reprobing ? 'Probing…' : 'Re-probe metadata only'}
                </button>
              </div>
            </details>
          </div>
        );
      })()}

      {/* ── CASCADE RESULT BANNER ───
          Replaces the misleading "Re-probed 0 ok · 0 failed (of 0
          targets)" UI. Shows the full backfill → reprobe story in one
          place with the actual numbers + the next-step hint. */}
      {cascadeResult && (
        <CascadeResultBanner
          result={cascadeResult}
          onDismiss={() => setCascadeResult(null)}
        />
      )}

      {/* ── RECOMPUTE FRAME COUNTS RESULT BANNER ─── */}
      {recomputeResult && (
        recomputeResult.error_title ? (
          <StructuredErrorBanner
            data-testid="admin-allotment-recompute-result"
            title={recomputeResult.error_title}
            message={recomputeResult.error_message}
            hint={recomputeResult.error_hint}
            onDismiss={() => setRecomputeResult(null)}
          />
        ) : (
        <div
          className={`rounded-md p-3 text-xs ${recomputeResult.ok
            ? 'bg-violet-500/10 border border-violet-500/30 text-violet-200'
            : 'bg-red-500/10 border border-red-500/30 text-red-200'}`}
          data-testid="admin-allotment-recompute-result"
        >
          {recomputeResult.ok ? (
            <>
              <p>
                Recomputed frames for{' '}
                <span className="font-bold">{recomputeResult.clips_updated}</span> clip{recomputeResult.clips_updated === 1 ? '' : 's'}.{' '}
                Propagated to {recomputeResult.tasks_updated} task{recomputeResult.tasks_updated === 1 ? '' : 's'} &{' '}
                {recomputeResult.registry_updated} registry row{recomputeResult.registry_updated === 1 ? '' : 's'}.
              </p>
              {Array.isArray(recomputeResult.sample_corrections) && recomputeResult.sample_corrections.length > 0 && (
                <details className="mt-1 text-[10px] opacity-80">
                  <summary className="cursor-pointer underline">
                    Show corrections (first {recomputeResult.sample_corrections.length})
                  </summary>
                  <ul className="mt-1 space-y-0.5">
                    {recomputeResult.sample_corrections.map((s, i) => (
                      <li key={i}>
                        <span className="font-mono">{s.label || s.clip_id.slice(0, 8)}</span>:{' '}
                        <span className="text-red-300">{s.old_frame_count}</span> →{' '}
                        <span className="text-emerald-300">{s.new_frame_count}</span> frames
                        <span className="opacity-60"> ({s.duration_sec}s @ {s.fps}fps)</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {recomputeResult.clips_updated === 0 && (
                <p className="mt-0.5 text-[10px] opacity-70">All clip frame counts were already correct — nothing to fix.</p>
              )}
            </>
          ) : (
            <p>Recompute failed: {recomputeResult.error || 'unknown error'}</p>
          )}
        </div>
        )
      )}

      {/* ── BACKFILL RESULT BANNER ─── */}
      {backfillResult && (
        backfillResult.error_title ? (
          <StructuredErrorBanner
            data-testid="admin-allotment-backfill-result"
            title={backfillResult.error_title}
            message={backfillResult.error_message}
            hint={backfillResult.error_hint}
            onDismiss={() => setBackfillResult(null)}
          />
        ) : (
        <div
          className={`rounded-md p-3 text-xs ${backfillResult.ok && backfillResult.still_missing === 0
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
            : backfillResult.ok
              ? 'bg-orange-500/10 border border-orange-500/30 text-orange-200'
              : 'bg-red-500/10 border border-red-500/30 text-red-200'}`}
          data-testid="admin-allotment-backfill-result"
        >
          <div className="flex items-start gap-2">
            {backfillResult.ok && backfillResult.still_missing === 0
              ? <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <p>
                Backfilled NAS paths · {backfillResult.repaired || 0} repaired ·{' '}
                {backfillResult.still_missing || 0} still missing (of{' '}
                {backfillResult.scanned || 0} broken clips).
              </p>
              {backfillResult.tldr && (
                <p className="mt-0.5 text-[10px] opacity-80">{backfillResult.tldr}</p>
              )}
              {Array.isArray(backfillResult.sample) && backfillResult.sample.length > 0 && (
                <details className="mt-1 text-[10px] opacity-80">
                  <summary className="cursor-pointer underline">
                    Show first {Math.min(backfillResult.sample.length, 10)} repairs / errors
                  </summary>
                  <ul className="mt-1 list-disc pl-4 max-h-32 overflow-y-auto">
                    {backfillResult.sample.slice(0, 10).map((s, i) => (
                      <li key={i}>
                        <span className="font-mono">{s.label || s.clip_id?.slice(0, 8)}</span>:{' '}
                        {s.error
                          ? <span className="text-red-300">{s.error}</span>
                          : <span className="text-emerald-300 truncate">{s.nas_video_rel}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <button
              onClick={() => setBackfillResult(null)}
              className="text-zinc-500 hover:text-zinc-300 shrink-0"
              data-testid="admin-allotment-backfill-dismiss"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
        )
      )}

      {/* ── RE-PROBE RESULT BANNER ───
          Jun 13 2026 root-cause fix: structured-error path renders a
          clean failure card with title + message + hint INSTEAD of
          the misleading "Re-probed 0 ok · 0 failed (of 0 targets)"
          headline (those zeros were React state defaults the user
          mistook for real data). The happy-path banner now only
          renders when we actually have totals to show. */}
      {reprobeResult && (
        reprobeResult.error_title ? (
          <StructuredErrorBanner
            data-testid="admin-allotment-reprobe-result"
            title={reprobeResult.error_title}
            message={reprobeResult.error_message}
            hint={reprobeResult.error_hint}
            onDismiss={() => setReprobeResult(null)}
          />
        ) : (
        <div
          className={`rounded-md p-3 text-xs ${reprobeResult._polling
            ? 'bg-blue-500/10 border border-blue-500/30 text-blue-200'
            : reprobeResult.ok && (reprobeResult.probe_failed || 0) === 0
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
              : reprobeResult.ok
                ? 'bg-orange-500/10 border border-orange-500/30 text-orange-200'
                : 'bg-red-500/10 border border-red-500/30 text-red-200'}`}
          data-testid="admin-allotment-reprobe-result"
        >
          <div className="flex items-start gap-2">
            {reprobeResult._polling
              ? <Loader2 className="w-3.5 h-3.5 mt-0.5 shrink-0 animate-spin" />
              : reprobeResult.ok && (reprobeResult.probe_failed || 0) === 0
                ? <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            <div className="min-w-0 flex-1">
              {reprobeResult._polling ? (
                <p>
                  Re-probing in background · {reprobeResult.probed_ok || 0} ok ·{' '}
                  {reprobeResult.probe_failed || 0} failed (of{' '}
                  {reprobeResult.total_targets || 0} targets) — keep this page open…
                </p>
              ) : (reprobeResult.total_targets || 0) === 0 ? (
                <p>
                  {(reprobeResult.missing_path_count || 0) > 0
                    ? `Nothing was re-probed because ${reprobeResult.missing_path_count} clip(s) have no NAS path. Click "Fix everything" above to repair them first.`
                    : 'All clips already have full metadata — nothing to re-probe.'}
                </p>
              ) : (
                <p>
                  Re-probed {reprobeResult.probed_ok || 0} ok ·{' '}
                  {reprobeResult.probe_failed || 0} failed (of {reprobeResult.total_targets} targets).
                </p>
              )}
              {reprobeResult.tldr && (
                <p className="mt-0.5 text-[10px] opacity-80">{reprobeResult.tldr}</p>
              )}
              {Array.isArray(reprobeResult.failures) && reprobeResult.failures.length > 0 && (
                <details className="mt-1 text-[10px] opacity-80">
                  <summary className="cursor-pointer underline">
                    Show first {Math.min(reprobeResult.failures.length, 5)} failure details
                  </summary>
                  <ul className="mt-1 list-disc pl-4 max-h-32 overflow-y-auto">
                    {reprobeResult.failures.slice(0, 5).map((f, i) => (
                      <li key={i}>
                        <span className="font-mono">{f.label}</span>: {f.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <button
              onClick={() => setReprobeResult(null)}
              className="text-zinc-500 hover:text-zinc-300 shrink-0"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
        )
      )}

      {/* ── SUMMARY BAR (5 cards now with total assigned secs) ─── */}
      {data && (
        <div className="grid grid-cols-5 gap-2" data-testid="admin-allotment-summary">
          <SummaryCard label="Total Clips" value={data.summary.total_clips} color="text-white" />
          <SummaryCard label="Layout Unassigned" value={data.summary.layout_unassigned} color="text-amber-400" />
          <SummaryCard label="Animation Unassigned" value={data.summary.animation_unassigned} color="text-rose-400" />
          <SummaryCard label="Workforce" value={workforce.length} icon={<Users className="w-3 h-3" />} color="text-blue-400" />
          <SummaryCard
            label="Assigned Time"
            value={fmtMinSec(workload?.total_seconds || 0)}
            color="text-emerald-400"
            testId="admin-allotment-summary-assigned-time"
          />
        </div>
      )}

      {/* ── PER-ARTIST WORKLOAD ───
          Founder ask (Feb 14, 2026): "we need this data, to see, how
          many secs/mins we have assigned to which team / animator —
          this is as important for Animator / freelancer / vendor."
          One glance: who has how much work. */}
      {workload && workload.rows && workload.rows.length > 0 && (
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-md"
          data-testid="admin-allotment-workload"
        >
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1.5">
              <Users className="w-3 h-3 text-emerald-400" />
              Workload · {workload.total_artists} artists · {fmtMinSec(workload.total_seconds)}
              {workload.total_seconds > 0 && (
                <span className="text-emerald-400/80 normal-case font-mono normal-tracking">
                  ({workload.total_seconds.toLocaleString()}s)
                </span>
              )}
              · {workload.total_tasks} tasks
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 p-2">
            {workload.rows.map(r => (
              <div
                key={r.user_id}
                className="bg-zinc-950/60 border border-zinc-800/60 rounded-sm px-2.5 py-2"
                data-testid={`workload-row-${r.user_id}`}
              >
                <div className="flex items-center justify-between gap-1.5">
                  <p className="text-[11px] text-zinc-200 truncate flex-1">
                    {r.full_name || '—'}
                  </p>
                  <WorkforceBadge user={r} />
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-base font-heading font-bold text-emerald-300"
                        data-testid={`workload-mins-${r.user_id}`}>
                    {fmtMinSec(r.assigned_seconds)}
                  </span>
                  {/* Raw total seconds — founder ask May 14, 2026:
                      "We need total secs also". The 'Nm Ns' format is
                      readable but PMs often want the raw second count
                      to plug into billing math or rate-card lookups. */}
                  {r.assigned_seconds > 0 && (
                    <span
                      className="text-[10px] text-emerald-400/80 font-mono"
                      data-testid={`workload-secs-${r.user_id}`}
                    >
                      ({r.assigned_seconds.toLocaleString()}s)
                    </span>
                  )}
                </div>
                <div className="text-[9px] text-zinc-500 mt-0.5">
                  {r.assigned_tasks} task{r.assigned_tasks === 1 ? '' : 's'}
                  {r.layout_count > 0 && r.animation_count > 0 && (
                    <span> · L:{r.layout_count} A:{r.animation_count}</span>
                  )}
                </div>
                {r.assigned_frames > 0 && (
                  <p className="text-[9px] text-zinc-600 mt-0.5">
                    {r.assigned_frames.toLocaleString()} frames
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TOOLBAR ─── */}
      {data && data.summary.total_clips > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-zinc-500" />
            {['unassigned', 'assigned', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`admin-allotment-filter-${f}`}
                className={`h-7 px-2.5 text-[10px] uppercase tracking-wider rounded-sm ${filter === f
                  ? 'bg-amber-500 text-zinc-950 font-bold'
                  : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'}`}
              >
                {f}
              </button>
            ))}
            <div className="relative ml-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
              <input
                placeholder="Search SH_###"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="admin-allotment-search"
                className="h-7 pl-7 pr-2 bg-zinc-900 border border-zinc-800 rounded-sm text-[11px] text-zinc-200 w-40"
              />
            </div>
          </div>
          {/* Bulk-stage scope picker — drives which task on each row the
              checkboxes target. Founder ask (Feb 28, 2026). */}
          <div
            className="flex items-center gap-1.5"
            data-testid="admin-allotment-bulk-scope"
          >
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Bulk scope</span>
            {['layout', 'animation', 'both'].map(s => (
              <button
                key={s}
                onClick={() => setBulkStage(s)}
                data-testid={`admin-allotment-bulk-scope-${s}`}
                className={`h-7 px-2.5 text-[10px] uppercase tracking-wider rounded-sm ${bulkStage === s
                  ? 'bg-violet-500 text-white font-bold'
                  : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── CLIPS TABLE ─── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center" data-testid="admin-allotment-loading">
            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
          </div>
        ) : !data || data.summary.total_clips === 0 ? (
          <EmptyState onScan={scanNAS} scanning={scanning} hasProject={!!projectId} />
        ) : filteredClips.length === 0 ? (
          <div className="p-10 text-center" data-testid="admin-allotment-no-match">
            <p className="text-xs text-zinc-500">No clips match the current filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-allotment-table">
              <thead>
                <tr className="bg-[#0d1430] text-white text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-2 py-2 text-center w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={eligibleSelectableIds.length === 0}
                      data-testid="admin-allotment-select-all"
                      title={`Select all visible (${eligibleSelectableIds.length})`}
                      className="w-3.5 h-3.5 accent-violet-500 cursor-pointer disabled:opacity-30"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Shot</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-2 py-2 text-center" title="Duration in seconds (ffprobe)">Dur</th>
                  <th className="px-2 py-2 text-center" title="Frames per second">FPS</th>
                  <th className="px-2 py-2 text-center" title="Total frame count">Frames</th>
                  <th className="px-2 py-2 text-center" title="Resolution width × height">Res</th>
                  <th className="px-3 py-2 text-left">Layout · Assignee</th>
                  <th className="px-3 py-2 text-center">Layout Status</th>
                  <th className="px-3 py-2 text-left">Animation · Assignee</th>
                  <th className="px-3 py-2 text-center">Animation Status</th>
                  <th className="px-3 py-2 text-center">NAS</th>
                </tr>
              </thead>
              <tbody>
                {filteredClips.map(c => {
                  const canSelect = eligibleSelectableIds.includes(c.cba_clip_id);
                  const isChecked = selectedClipIds.has(c.cba_clip_id);
                  return (
                  <tr
                    key={c.cba_clip_id}
                    className={`border-b border-zinc-800/40 hover:bg-zinc-800/30 ${isChecked ? 'bg-violet-500/5' : ''}`}
                    data-testid={`admin-allotment-row-${c.cba_clip_id}`}
                  >
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleRow(c.cba_clip_id)}
                        disabled={!canSelect}
                        data-testid={`admin-allotment-select-${c.cba_clip_id}`}
                        title={canSelect ? `Select for bulk ${bulkStage}` : `No ${bulkStage} task on this shot`}
                        className="w-3.5 h-3.5 accent-violet-500 cursor-pointer disabled:opacity-30"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-blue-400 font-bold">{c.label}</td>
                    <td className="px-3 py-2 text-[10px] text-zinc-500">
                      {c.manual_ingest ? (c.manual_ingest_kind === 'file' ? 'manual · file' : 'manual · folder') : 'auto-chop'}
                    </td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-300 text-[10px]">
                      {c.duration_sec ? `${Number(c.duration_sec).toFixed(2)}s` : '—'}
                    </td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-300 text-[10px]">
                      {c.fps ? Number(c.fps).toFixed(2) : '—'}
                    </td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-300 text-[10px]">
                      {c.frame_count > 0 ? c.frame_count.toLocaleString() : '—'}
                    </td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-500 text-[10px]">
                      {c.width && c.height ? `${c.width}×${c.height}` : '—'}
                    </td>

                    {/* Layout assignee */}
                    <AssigneeCell
                      task={c.layout}
                      cellId={`layout-${c.cba_clip_id}`}
                      onAssign={(uid) => assign(c.layout?.task_id, uid)}
                      onUnassign={() => unassign(c.layout?.task_id)}
                      pickerOpen={pickerOpen}
                      setPickerOpen={setPickerOpen}
                      workforce={workforce}
                      busy={busyTaskId === c.layout?.task_id}
                    />
                    <td className="px-3 py-2 text-center">
                      <StatusPill status={c.layout?.status} />
                    </td>

                    {/* Animation assignee */}
                    <AssigneeCell
                      task={c.animation}
                      cellId={`animation-${c.cba_clip_id}`}
                      onAssign={(uid) => assign(c.animation?.task_id, uid)}
                      onUnassign={() => unassign(c.animation?.task_id)}
                      pickerOpen={pickerOpen}
                      setPickerOpen={setPickerOpen}
                      workforce={workforce}
                      busy={busyTaskId === c.animation?.task_id}
                    />
                    <td className="px-3 py-2 text-center">
                      <StatusPill status={c.animation?.status} />
                    </td>

                    <td className="px-3 py-2 text-center">
                      {c.nas_video_rel && (
                        <span
                          className={`text-[10px] font-mono ${c.metadata_probed ? 'text-emerald-400' : 'text-zinc-600'}`}
                          title={c.metadata_probed ? `Probed: ${c.video_codec || '—'}${c.has_audio ? ` + ${c.audio_codec || '—'}` : ''} · ${(Number(c.size_bytes || 0) / 1024 / 1024).toFixed(1)} MB\n${c.nas_video_rel}` : c.nas_video_rel}
                        >
                          {c.metadata_probed ? 'NAS ✓ · probed' : 'NAS ✓'}
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── BULK ACTION BAR (floating, shows when ≥1 selection) ─── */}
      {selectedClipIds.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-zinc-950 border border-violet-500/40 rounded-lg shadow-2xl px-4 py-3 flex items-center gap-3 max-w-[95vw]"
          data-testid="admin-allotment-bulk-bar"
        >
          <div className="flex items-center gap-2 pr-3 border-r border-zinc-800">
            <span className="text-2xl font-heading font-bold text-violet-300" data-testid="admin-allotment-bulk-count">
              {selectedClipIds.size}
            </span>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 leading-tight">
              shot{selectedClipIds.size === 1 ? '' : 's'}<br />
              <span className="text-violet-300 font-bold">{bulkStage}</span> task{bulkStage === 'both' ? 's' : ''}
            </div>
          </div>
          <button
            onClick={() => setBulkOpen(true)}
            data-testid="admin-allotment-bulk-assign-btn"
            className="h-8 px-3 bg-violet-500 hover:bg-violet-400 text-white text-[11px] font-bold uppercase rounded flex items-center gap-1.5"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Assign to artist
          </button>
          <button
            onClick={bulkUnassign}
            disabled={bulkBusy}
            data-testid="admin-allotment-bulk-unassign-btn"
            className="h-8 px-3 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 text-[11px] font-bold uppercase rounded flex items-center gap-1.5 disabled:opacity-50"
          >
            {bulkBusy && bulkBusyAction === 'unassign'
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <X className="w-3.5 h-3.5" />}
            Unassign
          </button>
          <button
            onClick={() => setSelectedClipIds(new Set())}
            data-testid="admin-allotment-bulk-clear"
            className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[11px] uppercase rounded"
          >
            Clear
          </button>
        </div>
      )}

      {/* ── BULK ASSIGN MODAL ─── */}
      {bulkOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setBulkOpen(false)}
          data-testid="admin-allotment-bulk-modal"
        >
          <div
            className="bg-zinc-900 border border-violet-500/30 rounded-lg max-w-md w-full p-5 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-violet-200">
                  Bulk Assign <span className="capitalize">{bulkStage}</span>
                </h3>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Assign {bulkStage === 'both' ? 'Layout + Animation' : bulkStage} on{' '}
                  <span className="text-violet-300 font-bold">{selectedClipIds.size}</span> shot
                  {selectedClipIds.size === 1 ? '' : 's'} to one artist.
                </p>
              </div>
              <button onClick={() => setBulkOpen(false)} className="text-zinc-500 hover:text-zinc-300">
                <X className="w-4 h-4" />
              </button>
            </div>
            <select
              value={bulkUser}
              onChange={e => setBulkUser(e.target.value)}
              data-testid="admin-allotment-bulk-select"
              className="w-full h-9 bg-zinc-950 border border-zinc-700 rounded-md text-xs text-zinc-200 px-3"
            >
              <option value="">Pick an artist…</option>
              {(() => {
                const tag = u => {
                  const wt = (u.workforce_type || '').toLowerCase();
                  const role = (u.role || '').toLowerCase();
                  if (wt === 'vendor_producer' || role === 'vendor_producer') return '[VENDOR PROD]';
                  if (wt === 'vendor_artist' || role === 'vendor_artist') return '[VENDOR]';
                  if (wt === 'freelancer' || role === 'freelancer') return '[FREELANCER]';
                  return '[INTERNAL]';
                };
                const groups = { INTERNAL: [], FREELANCER: [], VENDOR: [] };
                workforce.forEach(u => {
                  const t = tag(u);
                  if (t.startsWith('[VENDOR')) groups.VENDOR.push(u);
                  else if (t === '[FREELANCER]') groups.FREELANCER.push(u);
                  else groups.INTERNAL.push(u);
                });
                return Object.entries(groups).filter(([, list]) => list.length).map(([label, list]) => (
                  <optgroup key={label} label={`${label} (${list.length})`}>
                    {list.map(u => (
                      <option key={u.id} value={u.id}>
                        {tag(u)} {u.full_name || u.email} · {(u.role || '').replace(/_/g, ' ')} · {u.active_task_count || 0} active
                      </option>
                    ))}
                  </optgroup>
                ));
              })()}
            </select>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setBulkOpen(false)}
                className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs rounded"
              >
                Cancel
              </button>
              <button
                onClick={bulkAssign}
                disabled={!bulkUser || bulkBusy}
                data-testid="admin-allotment-bulk-confirm"
                className="h-8 px-3 bg-violet-500 hover:bg-violet-400 text-white text-xs font-bold rounded disabled:opacity-50 flex items-center gap-1.5"
              >
                {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Assign {selectedClipIds.size} shot{selectedClipIds.size === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── tiny presentational pieces ─────────────────────────────────────── */

/**
 * fmtMinSec — render seconds as "Nm Ss" (e.g. "2m 30s") for cards & rows.
 * Returns "—" for zero / unknown so the UI doesn't show a noisy "0s".
 */
export function fmtMinSec(totalSeconds) {
  const s = Number(totalSeconds) || 0;
  if (!s) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${rem}s`;
}

/**
 * StructuredErrorBanner — shows a clean failure card for any API error
 * formatted via `formatApiError`. Critically, it never renders silent
 * zeros from a partially-set result object — if there's no `title`, we
 * fall back to a safe "Action failed" headline. This is the component
 * the founder's "Re-probed 0 of 0 targets — Token expired" bug led to.
 */
function StructuredErrorBanner({ title, message, hint, onDismiss, ...rest }) {
  return (
    <div
      className="rounded-md p-3 text-xs bg-red-500/10 border border-red-500/30 text-red-200"
      {...rest}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{title || 'Action failed'}</p>
          {message && (
            <p className="mt-0.5 text-[11px] opacity-90">{message}</p>
          )}
          {hint && (
            <p className="mt-1 text-[10px] opacity-70">{hint}</p>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-zinc-500 hover:text-zinc-300 shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * CascadeResultBanner — renders the unified "Fix Everything" result
 * (backfill → reprobe). Shows the full story in one banner so the user
 * never sees the misleading "0 of 0 targets" line they kept hitting.
 */
function CascadeResultBanner({ result, onDismiss }) {
  if (!result) return null;
  if (!result.ok) {
    return (
      <StructuredErrorBanner
        data-testid="admin-allotment-cascade-result"
        title={`Fix Everything failed at step '${result.step}'`}
        message={result.error_message || ''}
        hint={result.error_hint || ''}
        onDismiss={onDismiss}
      />
    );
  }
  const bf = result.backfill || {};
  const rp = result.reprobe || result.reprobe_progress || {};
  const running = result.running || result.step !== 'done';
  // When ANY reprobe failed, the banner should be amber, not emerald —
  // the founder's Jun 13 2026 report showed an emerald-coloured banner
  // proudly claiming "Metadata fix complete" while 33/33 reprobes
  // failed. Treat partial failures as warnings, full failures as red.
  const allFailed = !running && (rp.probe_failed || 0) > 0
    && (rp.probed_ok || 0) === 0 && (rp.total_targets || 0) > 0;
  const partialFailed = !running && (rp.probe_failed || 0) > 0
    && (rp.probed_ok || 0) > 0;
  const bannerCls = running
    ? 'bg-blue-500/10 border border-blue-500/30 text-blue-200'
    : allFailed
      ? 'bg-red-500/10 border border-red-500/30 text-red-200'
      : partialFailed
        ? 'bg-orange-500/10 border border-orange-500/30 text-orange-200'
        : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200';
  const headline = running
    ? 'Fixing metadata…'
    : allFailed
      ? 'Metadata fix failed — see why below'
      : partialFailed
        ? 'Metadata fix completed with errors'
        : 'Metadata fix complete';
  return (
    <div
      data-testid="admin-allotment-cascade-result"
      className={`rounded-md p-3 text-xs ${bannerCls}`}
    >
      <div className="flex items-start gap-2">
        {running
          ? <Loader2 className="w-3.5 h-3.5 mt-0.5 shrink-0 animate-spin" />
          : allFailed
            ? <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            : <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{headline}</p>
          <ul className="mt-1 space-y-0.5 text-[11px] opacity-90">
            <li>
              <span className="font-mono">Step 1 · Backfill NAS paths:</span>{' '}
              {bf.repaired ?? 0} repaired · {bf.still_missing ?? 0} still missing
              {' '}(of {bf.scanned ?? 0} broken)
            </li>
            <li>
              <span className="font-mono">Step 2 · Re-probe metadata:</span>{' '}
              {rp.probed_ok ?? 0} ok · {rp.probe_failed ?? 0} failed
              {' '}(of {rp.total_targets ?? 0} targets)
            </li>
          </ul>
          {/* Top-level diagnosis from the backend — categorises the
              dominant failure mode ("X/Y clips failed because the QNAP
              bridge isn't reachable" etc.). Surfacing this killed the
              guess-work for the founder's Jun 13 Thumbelina report. */}
          {rp.tldr && !running && (
            <p className="mt-1.5 text-[11px] font-medium opacity-95">
              {rp.tldr}
            </p>
          )}
          {rp.missing_path_count > 0 && (
            <p className="mt-1 text-[10px] text-amber-300/80">
              ⚠ {rp.missing_path_count} clip(s) still have no NAS path — the
              backfill couldn&apos;t find a matching file on NAS. Drop the
              missing files into the CBA folder and try again.
            </p>
          )}
          {/* Per-clip failure list — the founder's actual signal. The
              backend already returns up to 30 failures with the raw
              error string from the bridge / cloud probe. */}
          {Array.isArray(rp.failures) && rp.failures.length > 0 && !running && (
            <details className="mt-2 text-[10px] opacity-90" open={allFailed}>
              <summary className="cursor-pointer underline select-none">
                Show {Math.min(rp.failures.length, 30)} failure detail{rp.failures.length !== 1 ? 's' : ''}
              </summary>
              <ul className="mt-1.5 list-none space-y-1 max-h-56 overflow-y-auto pl-1">
                {rp.failures.slice(0, 30).map((f, i) => (
                  <li
                    key={i}
                    data-testid={`cascade-failure-row-${i}`}
                    className="flex gap-2 items-start font-mono"
                  >
                    <span className="text-zinc-300 shrink-0 min-w-[70px]">
                      {f.label || `clip_${i + 1}`}
                    </span>
                    <span className="text-red-300/90 break-all">
                      {f.error || '(no error message)'}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
        {onDismiss && !running && (
          <button
            onClick={onDismiss}
            className="text-zinc-500 hover:text-zinc-300 shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}


function SummaryCard({ label, value, color, icon, testId }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center"
         data-testid={testId}>
      <p className={`text-xl font-heading font-bold ${color}`}>
        {icon && <span className="inline-flex mr-1 align-middle">{icon}</span>}
        {value}
      </p>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1">{label}</p>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return <span className="text-[10px] text-zinc-600">—</span>;
  const map = {
    unassigned: { color: 'bg-zinc-700/40 text-zinc-300', label: 'Open' },
    not_started: { color: 'bg-blue-500/20 text-blue-300', label: 'Not Started' },
    blocked: { color: 'bg-red-500/15 text-red-300', label: 'Blocked' },
    in_progress: { color: 'bg-amber-500/20 text-amber-300', label: 'In Progress' },
    review: { color: 'bg-violet-500/20 text-violet-300', label: 'Review' },
    revise: { color: 'bg-orange-500/20 text-orange-300', label: 'Revise' },
    completed: { color: 'bg-emerald-500/20 text-emerald-300', label: 'Completed' },
    approved: { color: 'bg-emerald-500/20 text-emerald-300', label: 'Approved' },
    client_approved: { color: 'bg-emerald-500/30 text-emerald-200', label: 'Client OK' },
  };
  const m = map[status] || { color: 'bg-zinc-800 text-zinc-300', label: status };
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${m.color}`}>
      {m.label}
    </span>
  );
}

function AssigneeCell({ task, cellId, onAssign, onUnassign, pickerOpen, setPickerOpen, workforce, busy }) {
  if (!task) {
    return <td className="px-3 py-2 text-[10px] text-zinc-600">No task</td>;
  }
  const isOpen = pickerOpen === cellId;
  return (
    <td className="px-3 py-2 relative">
      <div className="flex items-center gap-2">
        {task.assigned_to_user_id ? (
          <>
            <span className="text-[11px] text-zinc-200 truncate" data-testid={`assignee-${cellId}`}>
              {task.assigned_to_name || '—'}
            </span>
            <span className="text-[9px] text-zinc-600 uppercase">{task.assigned_to_role}</span>
            <button
              onClick={onUnassign}
              disabled={busy}
              data-testid={`unassign-${cellId}`}
              className="text-zinc-500 hover:text-rose-300 disabled:opacity-50"
              title="Unassign"
            >
              <X className="w-3 h-3" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setPickerOpen(isOpen ? null : cellId)}
            disabled={busy}
            data-testid={`pick-${cellId}`}
            className="h-6 px-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-[10px] rounded flex items-center gap-1 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
            Assign
            <ChevronDown className="w-3 h-3" />
          </button>
        )}
      </div>
      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 w-80 max-h-80 overflow-y-auto bg-zinc-950 border border-amber-500/30 rounded-md shadow-2xl z-30"
          data-testid={`picker-${cellId}`}
        >
          <div className="px-3 py-1.5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
            <span className="text-[9px] font-bold uppercase tracking-widest text-amber-300">Assign artist</span>
            <button onClick={() => setPickerOpen(null)} className="text-zinc-500 hover:text-zinc-300">
              <X className="w-3 h-3" />
            </button>
          </div>
          {workforce.length === 0 ? (
            <div className="px-3 py-3 text-[10px] text-zinc-500">
              No eligible artists. Add team members in HR first.
            </div>
          ) : (
            (() => {
              const groups = [
                { key: 'internal', label: 'In-house Employees',
                  color: 'text-emerald-300',
                  filter: u => (u.workforce_type === 'employee') ||
                    (!u.workforce_type && !['freelancer', 'vendor_producer', 'vendor_artist'].includes(u.role)) },
                { key: 'freelancer', label: 'Freelancers',
                  color: 'text-amber-300',
                  filter: u => u.workforce_type === 'freelancer' || u.role === 'freelancer' },
                { key: 'vendor', label: 'Vendor Producers',
                  color: 'text-violet-300',
                  filter: u => u.workforce_type === 'vendor_producer' || u.role === 'vendor_producer'
                    || u.workforce_type === 'vendor_artist' || u.role === 'vendor_artist' },
              ];
              const sections = groups.map(g => ({ ...g, items: workforce.filter(g.filter) }))
                .filter(g => g.items.length > 0);
              return sections.map((g, idx) => (
                <div key={g.key}>
                  <div className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest ${g.color} bg-zinc-900/70 ${idx > 0 ? 'border-t border-zinc-800' : ''}`}>
                    {g.label} <span className="text-zinc-500">· {g.items.length}</span>
                  </div>
                  <ul>
                    {g.items.map(u => (
                      <li key={u.id}>
                        <button
                          onClick={() => onAssign(u.id)}
                          data-testid={`pick-${cellId}-${u.id}`}
                          className="w-full px-3 py-2 hover:bg-zinc-800 flex items-center justify-between gap-2 text-left"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-[11px] text-zinc-200 truncate">{u.full_name || u.email}</p>
                              <WorkforceBadge user={u} />
                            </div>
                            <p className="text-[9px] text-zinc-500">
                              {(u.role || '').replace(/_/g, ' ')}
                              {u.department ? ` · ${u.department}` : ''}
                              {' · '}{u.active_task_count || 0} active
                            </p>
                          </div>
                          <UserPlus className="w-3 h-3 text-amber-400 shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ));
            })()
          )}
        </div>
      )}
    </td>
  );
}

function EmptyState({ onScan, scanning, hasProject }) {
  if (!hasProject) {
    return (
      <div className="p-12 text-center" data-testid="admin-allotment-no-project">
        <Layers className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-sm text-zinc-500">Pick a project to start allotting CBA shots.</p>
      </div>
    );
  }
  return (
    <div className="p-12 text-center space-y-4" data-testid="admin-allotment-empty">
      <Film className="w-12 h-12 text-zinc-700 mx-auto" />
      <div>
        <p className="text-base text-zinc-300 font-semibold">No CBA clips on this project yet.</p>
        <p className="text-xs text-zinc-500 mt-1 max-w-md mx-auto">
          Drop SH###.mp4 files (or SH### folders) into the project&apos;s
          <span className="font-mono text-emerald-400 mx-1">01_Pre_Production/004_Cut_by_Animatics/</span>
          on the NAS, then click below to ingest them.
        </p>
      </div>
      <button
        onClick={onScan}
        disabled={scanning}
        data-testid="admin-allotment-scan-empty"
        className="h-10 px-5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-sm font-bold rounded-md inline-flex items-center gap-2 disabled:opacity-50"
      >
        {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderSync className="w-4 h-4" />}
        {scanning ? 'Scanning NAS…' : 'Scan NAS for manual cuts'}
      </button>
    </div>
  );
}
